import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useDrag } from 'react-dnd';
import { RotateCw, Trash2, GripVertical } from 'lucide-react';

const TextBoxShape = ({
  textBox,
  zoom = 1,
  isSelected = false,
  onClick,
  onUpdate,
  onDelete,
  gridSize = 20,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(textBox.text);
  const [isDragging, setIsDragging] = useState(false);
  const [isRotating, setIsRotating] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const textAreaRef = useRef(null);
  const boxRef = useRef(null);

  const snapToGrid = useCallback((value) => {
    return Math.round(value / gridSize) * gridSize;
  }, [gridSize]);

  // Handle double-click to edit text
  const handleDoubleClick = (e) => {
    e.stopPropagation();
    setIsEditing(true);
    setEditText(textBox.text);
  };

  // Save text on blur or Enter
  const handleTextSave = () => {
    setIsEditing(false);
    if (editText !== textBox.text) {
      onUpdate(textBox.id, { text: editText });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleTextSave();
    }
    if (e.key === 'Escape') {
      setIsEditing(false);
      setEditText(textBox.text);
    }
  };

  // Focus textarea when editing starts
  useEffect(() => {
    if (isEditing && textAreaRef.current) {
      textAreaRef.current.focus();
      textAreaRef.current.select();
    }
  }, [isEditing]);

  // Drag handling
  const handleMouseDown = useCallback((e) => {
    if (isEditing) return;
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    
    onClick?.(textBox.id);
    setIsDragging(true);
    setDragStart({
      mouseX: e.clientX,
      mouseY: e.clientY,
      startX: textBox.position_x,
      startY: textBox.position_y,
    });
  }, [isEditing, onClick, textBox]);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !dragStart) return;
    
    const dx = (e.clientX - dragStart.mouseX) / zoom;
    const dy = (e.clientY - dragStart.mouseY) / zoom;
    
    const newX = snapToGrid(dragStart.startX + dx);
    const newY = snapToGrid(dragStart.startY + dy);
    
    onUpdate(textBox.id, { position_x: newX, position_y: newY });
  }, [isDragging, dragStart, zoom, snapToGrid, onUpdate, textBox.id]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setDragStart(null);
  }, []);

  // Rotation handling
  const handleRotateStart = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsRotating(true);
    
    const rect = boxRef.current?.getBoundingClientRect();
    if (rect) {
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      setDragStart({
        centerX,
        centerY,
        startRotation: textBox.rotation || 0,
        startAngle: Math.atan2(e.clientY - centerY, e.clientX - centerX) * (180 / Math.PI),
      });
    }
  }, [textBox.rotation]);

  const handleRotateMove = useCallback((e) => {
    if (!isRotating || !dragStart) return;
    
    const angle = Math.atan2(e.clientY - dragStart.centerY, e.clientX - dragStart.centerX) * (180 / Math.PI);
    const deltaAngle = angle - dragStart.startAngle;
    let newRotation = Math.round((dragStart.startRotation + deltaAngle) / 15) * 15; // Snap to 15 degrees
    
    // Normalize to 0-360
    newRotation = ((newRotation % 360) + 360) % 360;
    
    onUpdate(textBox.id, { rotation: newRotation });
  }, [isRotating, dragStart, onUpdate, textBox.id]);

  const handleRotateEnd = useCallback(() => {
    setIsRotating(false);
    setDragStart(null);
  }, []);

  // Global mouse events
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  useEffect(() => {
    if (isRotating) {
      window.addEventListener('mousemove', handleRotateMove);
      window.addEventListener('mouseup', handleRotateEnd);
      return () => {
        window.removeEventListener('mousemove', handleRotateMove);
        window.removeEventListener('mouseup', handleRotateEnd);
      };
    }
  }, [isRotating, handleRotateMove, handleRotateEnd]);

  const width = textBox.width || 150;
  const height = textBox.height || 40;

  return (
    <div
      ref={boxRef}
      className={`absolute cursor-move ${isSelected ? 'ring-2 ring-yellow-400 ring-offset-1' : ''}`}
      style={{
        left: textBox.position_x * zoom,
        top: textBox.position_y * zoom,
        width: width * zoom,
        height: height * zoom,
        transform: `rotate(${textBox.rotation || 0}deg)`,
        transformOrigin: 'center center',
        zIndex: isSelected ? 100 : 10,
      }}
      onMouseDown={handleMouseDown}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.(textBox.id);
      }}
      onDoubleClick={handleDoubleClick}
      data-testid={`text-box-${textBox.id}`}
    >
      {/* Text Box Content */}
      <div
        className="w-full h-full flex items-center justify-center rounded"
        style={{
          backgroundColor: textBox.background_color || 'transparent',
          border: isSelected ? '1px dashed #facc15' : '1px dashed transparent',
          padding: '4px',
        }}
      >
        {isEditing ? (
          <textarea
            ref={textAreaRef}
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            onBlur={handleTextSave}
            onKeyDown={handleKeyDown}
            className="w-full h-full bg-white/90 border border-gray-300 rounded px-2 py-1 resize-none focus:outline-none focus:ring-2 focus:ring-yellow-400"
            style={{
              fontSize: `${(textBox.font_size || 16) * zoom}px`,
              color: textBox.font_color || '#000000',
              textAlign: textBox.text_align || 'center',
            }}
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span
            className="select-none whitespace-pre-wrap break-words overflow-hidden"
            style={{
              fontSize: `${(textBox.font_size || 16) * zoom}px`,
              color: textBox.font_color || '#000000',
              textAlign: textBox.text_align || 'center',
              width: '100%',
              display: 'block',
            }}
          >
            {textBox.text || 'Text'}
          </span>
        )}
      </div>

      {/* Controls when selected */}
      {isSelected && !isEditing && (
        <>
          {/* Rotation handle */}
          <div
            className="absolute -top-8 left-1/2 transform -translate-x-1/2 w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center cursor-grab hover:bg-yellow-500 shadow-md"
            onMouseDown={handleRotateStart}
            title="Drag to rotate"
          >
            <RotateCw className="w-3 h-3 text-slate-900" />
          </div>

          {/* Delete button */}
          <div
            className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center cursor-pointer hover:bg-red-600 shadow-md"
            onClick={(e) => {
              e.stopPropagation();
              onDelete?.(textBox.id);
            }}
            title="Delete text box"
          >
            <Trash2 className="w-3 h-3 text-white" />
          </div>

          {/* Drag indicator */}
          <div className="absolute -left-2 top-1/2 transform -translate-y-1/2 text-yellow-400">
            <GripVertical className="w-4 h-4" />
          </div>
        </>
      )}
    </div>
  );
};

export default TextBoxShape;
